import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { FIELD_FORMATS } from '../constants'; // Reusing formats from Dataset Table logic
import { MotfiFullScreenDialogTitle } from 'BaseComponents/MotifFullScreenDialog/MotfiFullScreenDialogTitle';
import { MotifFullScreenDialog } from 'BaseComponents/MotifFullScreenDialog/MotifFullScreenDialog';
import { MotifFullScreenDialogActions } from 'BaseComponents/MotifFullScreenDialog/MotifFullScreenDialogActions';
import { MotifFullScreenDialogContent } from 'BaseComponents/MotifFullScreenDialog/MotifFullScreenDialogContent';
import { activeOrgSelector } from 'redux/reducer/AuthenticationReducer';
import { useSelector } from 'react-redux';
import { useUpdateOrganizationMasterRuntimeParameter, useUpdateQuestionParameter } from 'hooks/questionAPIs';


export const EditRuntimeParameterDialog = ({
  open, row, onClose, onSuccess,
}) => {
  const { ID: currentOrgID } = useSelector(activeOrgSelector);
  const {
    mutate: updateSyncParameter, isLoading: isUpdatingSyncParameter,
  } = useUpdateOrganizationMasterRuntimeParameter();
  const {
    mutate: updateParameter, isLoading: isUpdatingParameter,
  } = useUpdateQuestionParameter();

  // Permissions Check
  const isSynced = row?.isSynced;
  const isIndependent = !isSynced;
  const isNonOwnerSynced = isSynced && row?.masterOwnerEntityID !== currentOrgID;
  const isOwnerSynced = isSynced && row?.masterOwnerEntityID === currentOrgID;

  const canEdit = isOwnerSynced || isIndependent;
  const isSaving = isUpdatingSyncParameter || isUpdatingParameter;
  const isSaveDisabled = isSaving || !canEdit;

  const [values, setValues] = useState({
    displayName: '',
    description: '',
    format: '',
    exampleValue: '',
  });

  // Reset form when row changes
  useEffect(() => {
    if (row) {
      setValues({
        displayName: row.displayName || '',
        description: row.description || '',
        format: row.format || '',
        exampleValue: row.exampleValue || '',
      });
    }
  }, [row]);

  const handleChange = (field) => (e) => {
    setValues(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSuccess = () => {
    onSuccess?.();
    onClose();
  };

  const handleSave = () => {
    if (!row) return;

    // Update synced parameter
    if (row.isSynced && row.masterRecordID) {
      // Prevent edits to synced parameters owned by another organization
      if (!isOwnerSynced) return;

      updateSyncParameter(
        {
          master: {
            // Fields required by API to identify RTPs to sync
            ID: row.masterRecordID,
            name: row.name,
            type: row.type,
            // Editable fields
            displayName: values.displayName,
            description: values.description,
            format: values.format,
            exampleValue: values.exampleValue,
          },
        },
        {
          onSuccess: handleSuccess,
        },
      );
    }
    else {
      // Update independent (non-synced) parameter

      // Extract the first question ID from the IDs array (independent params only have 1)
      const questionID = row.questions && row.questions.length > 0 ? row.questions[0].ID : null;

      if (!questionID) {
        return;
      }

      updateParameter(
        {
          // Fields required by API to identify the RTP to edit
          questionID,
          name: row.name,
          // Editable fields
          displayName: values.displayName,
          description: values.description,
          format: values.format,
          exampleValue: values.exampleValue,
        },
        {
          onSuccess: handleSuccess,
        },
      );
    }
  };

  return (
    <MotifFullScreenDialog
      open={open}
      onClose={onClose}
      anchor='bottom'
      style={{ margin: '6.25rem 0px 0px' }}
    >
      <MotfiFullScreenDialogTitle onClose={onClose}>
        Edit Parameter:
        {' '}
        {row?.name || ''}
      </MotfiFullScreenDialogTitle>
      <MotifFullScreenDialogContent>
        <Box
          display='grid'
          gridTemplateColumns='1fr 3fr 1fr'
          gridTemplateAreas='". one ."'
        >
          <Grid container justifyContent='space-between' spacing={1} gridArea='one'>
            {/* Scenario 1: Synced by Partner (Read Only) */}
            {isNonOwnerSynced && (
              <Alert severity='warning' sx={{ mb: 3 }}>
                <Typography>
                  This parameter is synced by another organization. You cannot edit it here.
                  To make local changes, use the Question Builder (which will unsync it).
                </Typography>
              </Alert>
            )}

            {/* Scenario 2: Synced Owner (Editable with Cascade Warning) */}
            {isOwnerSynced && (
              <Grid item xs={12}>
                <Alert severity='info' sx={{ mb: 3 }}>
                  <Typography>
                    Note:
                    {' '}
                    Changes will apply to
                    {' '}
                    <strong>{row.usageCount}</strong>
                    {' '}
                    question
                    {row.usageCount > 1 ? 's' : ''}
                    {' '}
                    in this Clean Room
                    {row.orgUsageCount > row.usageCount && ` and ${row.orgUsageCount} total across your organization`}
                    .
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Scenario 3: Independent (Editable with Info) */}
            {isIndependent && (
              <Grid item xs={12}>
                <Alert severity='info' sx={{ mb: 3 }}>
                  <Typography>
                    This parameter is independent.
                    {' '}
                    You can edit Description, Format, and Example Value here.
                    To sync it across multiple questions, use the
                    {' '}
                    <strong>&quot;Sync&quot;</strong>
                    {' '}
                    action from the table menu.
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid container spacing={3}>
              {/* LOCKED FIELDS */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label='Parameter Name'
                  value={row?.name || ''}
                  disabled
                  helperText='To change Name, use Question Builder'
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label='Type'
                  value={row?.type || ''}
                  disabled
                  helperText='To change Type, use Question Builder'
                />
              </Grid>

              {/* EDITABLE FIELDS */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Display Name'
                  value={values.displayName}
                  onChange={handleChange('displayName')}
                  disabled={!canEdit}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label='Description'
                  value={values.description}
                  onChange={handleChange('description')}
                  disabled={!canEdit}
                />
              </Grid>

              {/* Format (Conditional) */}
              {(row?.type === 'DATE' || row?.type === 'TIMESTAMP') && (
                <Grid item xs={12}>
                  <TextField
                    select // Make it a dropdown if you imported FIELD_FORMATS
                    SelectProps={{ native: true }}
                    fullWidth
                    label='Format'
                    value={values.format}
                    onChange={handleChange('format')}
                    disabled={!canEdit}
                    helperText='e.g., YYYY-MM-DD'
                  >
                    <option value=''>Select Format</option>
                    {FIELD_FORMATS[row.type]?.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Example Value'
                  value={values.exampleValue}
                  onChange={handleChange('exampleValue')}
                  disabled={!canEdit}
                />
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </MotifFullScreenDialogContent>
      <MotifFullScreenDialogActions>
        <Button onClick={onClose} color='primary' variant='outlined'>
          Close
        </Button>
        <Button
          color='primary'
          variant='contained'
          {...{
            ...(isSaveDisabled ? {
              disabled: true,
            } : {
              onClick: handleSave,
            }),
          }}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </MotifFullScreenDialogActions>
    </MotifFullScreenDialog>
  );
};
